import { useMemo, useState } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';
import { useRows } from '@/hooks/useLive';
import { useSiteFilter } from '@/hooks/useSiteFilter';
import { useAuthStore } from '@/stores/authStore';
import { findEntity } from '@/config/masterData';
import { EntityManager } from '@/components/crud/EntityManager';
import type { Site } from '@/types/models';

/** Branch-owned shifts and operators; shared reference lists remain quarry-managed. */
export function DiaryLists() {
  const { entity } = useParams();
  const sites = useRows<Site>('sites', 'name');
  const siteOk = useSiteFilter();
  const role = useAuthStore((s) => s.profile?.role);
  const selected = useAuthStore((s) => s.branchId());
  const list = (sites ?? []).filter((s) => !s.deleted_at && s.is_active && siteOk(s.id));
  const [choice, setChoice] = useState('');
  const site = list.find((s) => s.id === choice) ?? list.find((s) => s.id === selected) ?? list[0];
  const def = findEntity(entity === 'operators' ? 'operators' : 'shifts')!;
  // The site is chosen above, so the form does not ask for it again.
  const scoped = useMemo(() => ({
    ...def, fields: def.fields.filter((f) => f.name !== 'site_id'), fixed: { site_id: site?.id ?? '' },
  }), [def, site?.id]);
  if (entity !== 'shifts' && entity !== 'operators') return <Navigate to="/diary/lists/shifts" replace />;
  if (!site) return <div className="placeholder">No branch available. Sync and try again.</div>;
  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        {list.length > 1 ? <select aria-label="Diary lists site" value={site.id} onChange={(e) => setChoice(e.target.value)}>
          {list.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select> : <strong>{site.name}</strong>}
        {(['shifts', 'operators'] as const).map((key) => (
          <NavLink key={key} to={`/diary/lists/${key}`}
            className={({ isActive }) => `btn ${isActive ? 'btn--primary' : 'btn--secondary'}`}>
            {key === 'shifts' ? 'Shifts' : 'Operators'}
          </NavLink>
        ))}
      </div>
      <EntityManager key={`${site.id}-${entity}`} entity={scoped} canEdit={role === 'manager' || role === 'admin'} />
    </div>
  );
}
